import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  useGetSchedulingBookingsQuery,
} from '../endpoints/scheduling.endpoints';
import type { SchedulingRequest } from '../types/scheduling.types';

function getMonthDateRange(month: Date) {
  const start = dayjs(month).startOf('month');
  const end = dayjs(month).endOf('month');
  return {
    start_date: start.format('YYYY-MM-DD'),
    end_date: end.format('YYYY-MM-DD'),
  };
}

export function groupRequestsByDay(requests: SchedulingRequest[]): Record<string, SchedulingRequest[]> {
  return requests.reduce<Record<string, SchedulingRequest[]>>((accumulator, request) => {
    const dayKey = dayjs(request.scheduled_at).format('YYYY-MM-DD');
    if (!accumulator[dayKey]) {
      accumulator[dayKey] = [];
    }
    accumulator[dayKey].push(request);
    return accumulator;
  }, {});
}

export function useSchedulingRequests() {
  const [visibleMonth, setVisibleMonth] = useState(() => dayjs().startOf('month').toDate());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const dateRange = useMemo(() => getMonthDateRange(visibleMonth), [visibleMonth]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetSchedulingBookingsQuery(dateRange);

  const requests = data?.results ?? [];
  const requestsByDay = useMemo(() => groupRequestsByDay(requests), [requests]);

  const selectedDayRequests = selectedDay ? requestsByDay[selectedDay] ?? [] : [];

  const goToPreviousMonth = () => {
    setVisibleMonth((current) => dayjs(current).subtract(1, 'month').toDate());
    setSelectedDay(null);
  };

  const goToNextMonth = () => {
    setVisibleMonth((current) => dayjs(current).add(1, 'month').toDate());
    setSelectedDay(null);
  };

  return {
    visibleMonth,
    selectedDay,
    setSelectedDay,
    requests,
    requestsByDay,
    selectedDayRequests,
    isLoading: isLoading || isFetching,
    isError,
    error,
    refetch,
    goToPreviousMonth,
    goToNextMonth,
  };
}
